import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Star, ArrowRight, ShieldCheck, Clock, Wallet } from "lucide-react";

const faqs = (name, price) => [
  {
    q: `How much does it cost to get ${name.toLowerCase()} quotes?`,
    a: "Nothing. Homeowners post a project and receive quotes for free — LeadTitan is paid by the professionals, never by you.",
  },
  {
    q: "How fast will pros contact me?",
    a: "Matching pros in your ZIP code are alerted the moment you post. Most projects get their first reply within a few hours.",
  },
  {
    q: `What do ${name.toLowerCase()} pros pay for a lead?`,
    a: `Pros pay $${price} to unlock a ${name.toLowerCase()} lead, or use a monthly plan that includes a set number of unlocks. No commission is ever taken from the job itself.`,
  },
  {
    q: "Are the professionals vetted?",
    a: "Every pro applies for approval before they can buy leads, and homeowners rate them after the work. Ratings and review counts are shown on each profile.",
  },
];

export default function TradeLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/trades/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-bone">
        <Navbar />
        <div className="max-w-[900px] mx-auto px-6 py-24 text-center">
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Trade not found</h1>
          <Link to="/categories" className="brut-btn-primary mt-8 inline-flex" data-testid="trade-back-categories">
            Browse all trades
          </Link>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { category: cat, stats, pros, related } = data;
  const price = cat.lead_price.toFixed(0);
  const questions = faqs(cat.name, price);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        serviceType: cat.name,
        provider: { "@type": "Organization", name: "LeadTitan" },
        areaServed: "United States",
        description: `Get matched with vetted local ${cat.name.toLowerCase()} professionals on LeadTitan.`,
      },
      {
        "@type": "FAQPage",
        mainEntity: questions.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-bone">
      <Seo
        title={`${cat.name} Pros Near You`}
        path={`/trades/${cat.slug}`}
        description={`Get free quotes from vetted local ${cat.name.toLowerCase()} pros. Describe your project once and matched professionals reach out — homeowners never pay a fee.`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navbar />

      <section className="border-b-2 border-ink">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="overline mb-3">// {cat.name}</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.92]">
              Hire a {cat.name}
              <br />
              <span className="bg-acid px-2">pro near you.</span>
            </h1>
            <p className="text-base text-muted-foreground mt-6 max-w-xl leading-relaxed">
              Describe the job once. Vetted {cat.name.toLowerCase()} professionals in your ZIP code get
              alerted and reach out with real quotes. Free for homeowners, always.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/customer/post", { state: { category_id: cat.category_id } })}
                className="brut-btn-primary"
                data-testid="trade-post-cta"
              >
                Post my {cat.name.toLowerCase()} project <ArrowRight className="w-4 h-4 ml-2" />
              </button>
              <Link to="/pricing" className="brut-btn" data-testid="trade-pro-cta">
                I'm a {cat.name.toLowerCase()} pro
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-0 border-2 border-ink mt-10 max-w-lg">
              <Fact label="Open projects" v={stats.open_jobs} />
              <Fact label="Posted / 30d" v={stats.jobs_30d} />
              <Fact label="Pros in trade" v={stats.pros} last />
            </div>
          </div>

          <div className="lg:col-span-5 space-y-4">
            <Panel Icon={ShieldCheck} title="Vetted professionals"
              body="Every pro is approved before they can buy leads, and rated by homeowners afterwards." />
            <Panel Icon={Clock} title="Replies in hours"
              body={`Matched ${cat.name.toLowerCase()} pros are alerted the second your project goes live.`} />
            <Panel Icon={Wallet} title={`Pros pay $${price} per lead`}
              body="No commission on the job. You never pay LeadTitan anything." acid />
          </div>
        </div>
      </section>

      <section className="border-b-2 border-ink bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
          <div className="overline mb-3">// Three steps</div>
          <h2 className="font-display font-black text-3xl uppercase tracking-tighter">
            How {cat.name.toLowerCase()} jobs get done here
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-ink mt-8">
            {[
              ["01", "Describe the job", `Tell us the ${cat.name.toLowerCase()} work, your timeline and budget. Photos help pros quote accurately.`],
              ["02", "Pros reach out", "Approved local professionals unlock your project and message you with their approach and price."],
              ["03", "Pick your pro", "Compare ratings and quotes, chat in the app, then hire whoever fits. Rate them when it's done."],
            ].map(([n, t, b], i) => (
              <div key={n} className={`p-8 ${i < 2 ? "border-r-2 border-ink" : ""}`}>
                <div className="font-display font-black text-4xl text-acid [-webkit-text-stroke:2px_#111]">{n}</div>
                <div className="font-display font-bold uppercase text-lg mt-4">{t}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {pros.length > 0 && (
        <section className="border-b-2 border-ink">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
            <div className="overline mb-3">// Local talent</div>
            <h2 className="font-display font-black text-3xl uppercase tracking-tighter">
              {cat.name} pros on LeadTitan
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-ink mt-8">
              {pros.map((p, i) => (
                <Link key={p.user_id} to={`/pros/${p.user_id}`}
                  className={`p-6 bg-white hover:bg-acid transition-colors ${i < pros.length - 1 ? "border-r-2 border-b-2 md:border-b-0 border-ink" : ""}`}
                  data-testid={`trade-pro-${p.user_id}`}>
                  {p.featured && <div className="overline bg-acid border-2 border-ink inline-block px-2 py-[2px] mb-2">Featured</div>}
                  <div className="font-display font-bold uppercase text-lg">{p.name}</div>
                  {p.company && <div className="font-mono text-xs mt-1">{p.company}</div>}
                  <div className="flex items-center gap-1 mt-3">
                    {[...Array(5)].map((_, s) => (
                      <Star key={`${p.user_id}-s-${s}`} className={`w-3 h-3 ${s < Math.round(p.rating_avg) ? "fill-ink" : ""}`} />
                    ))}
                    <span className="font-mono text-[10px] ml-1">{p.rating_avg.toFixed(1)} ({p.review_count})</span>
                  </div>
                  {p.bio && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{p.bio}</p>}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-b-2 border-ink bg-white">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 py-16">
          <div className="overline mb-3">// Questions</div>
          <h2 className="font-display font-black text-3xl uppercase tracking-tighter">
            {cat.name}, answered
          </h2>
          <div className="mt-8 border-2 border-ink" data-testid="trade-faq">
            {questions.map((f, i) => (
              <div key={f.q} className={i < questions.length - 1 ? "border-b-2 border-ink" : ""}>
                <div className="p-6">
                  <div className="font-display font-bold uppercase text-base">{f.q}</div>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b-2 border-ink bg-ink text-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14 flex flex-wrap items-center justify-between gap-6">
          <h2 className="font-display font-black text-3xl uppercase tracking-tighter">
            Ready for {cat.name.toLowerCase()} quotes?
          </h2>
          <button
            onClick={() => navigate("/customer/post", { state: { category_id: cat.category_id } })}
            className="brut-btn-acid"
            data-testid="trade-post-cta-bottom"
          >
            Post my project — free <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14">
          <div className="overline mb-4">// Other trades</div>
          <div className="flex flex-wrap gap-2" data-testid="trade-related">
            {related.map((r) => (
              <Link key={r.slug} to={`/trades/${r.slug}`}
                className="border-2 border-ink bg-white hover:bg-acid px-3 py-2 font-mono text-[11px] uppercase tracking-widest"
                data-testid={`trade-related-${r.slug}`}>
                {r.name}
              </Link>
            ))}
            <Link to="/categories" className="border-2 border-ink bg-ink text-acid px-3 py-2 font-mono text-[11px] uppercase tracking-widest">
              All trades
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Fact = ({ label, v, last }) => (
  <div className={`p-4 bg-white ${!last ? "border-r-2 border-ink" : ""}`}>
    <div className="font-display font-black text-2xl">{v}</div>
    <div className="overline mt-1">{label}</div>
  </div>
);

const Panel = ({ Icon, title, body, acid }) => (
  <div className={`brut-card p-5 ${acid ? "bg-acid" : "bg-white"}`}>
    <Icon className="w-5 h-5" />
    <div className="font-display font-bold uppercase text-base mt-3">{title}</div>
    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{body}</p>
  </div>
);
